import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Star, ChevronRight } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Compact subscription upsell shown on the /plan wizard.
 * Displayed in Step 1 (right after the space grid) and Step 3
 * (right before the pay buttons) so subscribers see the savings
 * they'd get by upgrading before paying full price.
 *
 * Props:
 *  - plans?: array — optional pre-fetched plans; if omitted, the
 *    component fetches them itself so it can be dropped anywhere.
 */
export default function SubscriptionUpsell({ plans: plansProp }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [plans, setPlans] = useState(plansProp || []);

  useEffect(() => {
    if (plansProp && plansProp.length) return;
    (async () => {
      try {
        const r = await axios.get(`${API}/subscriptions/plans`);
        setPlans(r.data || []);
      } catch { /* ignore */ }
    })();
  }, [plansProp]);

  if (!plans || plans.length === 0) return null;

  return (
    <div
      className="bg-[#F9F9F7] rounded-xl border border-[#E2E2DF] px-4 py-4"
      data-testid="mini-subscriptions"
    >
      <div className="flex items-center gap-3 mb-3">
        <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[#C77D63]/15 flex items-center justify-center">
          <Star className="w-4 h-4 text-[#C77D63]" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-[#1A3C34] leading-tight">
            {t('landing.subBannerTitle', "Passez à l'abonnement")}
          </div>
          <div className="text-xs text-[#737373] leading-snug">
            {t('spaces.miniSubHint', "Économisez jusqu'à 30 % sur toutes vos réservations.")}
          </div>
        </div>
        <button
          onClick={() => navigate('/abonnements')}
          className="text-xs font-semibold text-[#C77D63] hover:text-[#b06a51] whitespace-nowrap flex items-center gap-1 flex-shrink-0"
          data-testid="mini-sub-cta"
        >
          {t('landing.discoverSubs', 'Découvrir')} <ChevronRight className="w-3.5 h-3.5" />
        </button>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {plans.slice(0, 3).map((p) => (
          <div
            key={p.plan_id}
            className="bg-white border border-[#E2E2DF] rounded-md px-2.5 py-1 text-[11px]"
          >
            <span className="font-semibold text-[#1A3C34]">{p.name}</span>
            <span className="text-[#737373] mx-1">·</span>
            <span className="font-mono font-bold text-black">
              {p.price_monthly}€/mois
            </span>
            <span className="ml-1.5 text-[#C77D63] font-semibold">
              -{p.discount_percent}%
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
